export interface QueueDto {
  tripRequestId: number;
  recipientId: number;
  tripConfirmationNo: string | null;
  fullName: string | null;
  payeeName: string | null;
  appointmentDate: string | null;
  submittedDate: string | null;
  dueDate: string | null;
  createdBy: string | null;
  aging: string | null;
  agingCode: AgingCode | null;
  requestStatus: string | null;
  amountDue: string | null;
  queueStatusMappingId: number | null;
  daysRemaining: number | null;
  followUpDate: string | null;
  assignedTo?: string | null;
}

export const QUEUE_STATUS_MAP = {
  REQUEST_QUEUE: {
    SUBMITTED_BY_AGENCY: 1,
    SUBMITTED_BY_CONSUMER: 2,
    SUBMITTED_DOCUMENTATION_PENDING: 3,
    UNDER_REVIEW: 4,
    PENDING: 5,
    NO_RESPONSE: 15,
    RETURNED:18,
  },
  EXCEPTION_QUEUE: {
    SUBMITTED_BY_AGENCY: 6,
    SUBMITTED_BY_CONSUMER: 7,
    SUBMITTED_DOCUMENTATION_PENDING: 8,
    UNDER_REVIEW: 9,
    PENDING: 10,
    NO_RESPONSE: 16,
    RETURNED:19,
  },
  FISCAL_QUEUE: {
    PENDING_PAYMENTS: 11,
    PAYMENTS_COMPLETED: 12,
  },
  MY_QUEUE: {
    UNDER_REVIEW: 13,
    PENDING: 14,
    NO_RESPONSE: 17,
    RETURNED: 21,
  },
  AGING_QUEUE: {
    EXPIRED: 20,
  },  
} as const; 

export const AGING_GROUP_IDS = [
  ...Object.values(QUEUE_STATUS_MAP.REQUEST_QUEUE),
  ...Object.values(QUEUE_STATUS_MAP.EXCEPTION_QUEUE),
  QUEUE_STATUS_MAP.FISCAL_QUEUE.PENDING_PAYMENTS,
  QUEUE_STATUS_MAP.AGING_QUEUE.EXPIRED,
] as number[];
export interface AgingGroup {
  label: string;
  ids: number[];
}

export const AGING_GROUPS: AgingGroup[] = [
  {
    label: 'Submitted by agency',
    ids: [QUEUE_STATUS_MAP.REQUEST_QUEUE.SUBMITTED_BY_AGENCY, QUEUE_STATUS_MAP.EXCEPTION_QUEUE.SUBMITTED_BY_AGENCY],
  },
  {
    label: 'Submitted by consumer',
    ids: [QUEUE_STATUS_MAP.REQUEST_QUEUE.SUBMITTED_BY_CONSUMER, QUEUE_STATUS_MAP.EXCEPTION_QUEUE.SUBMITTED_BY_CONSUMER],
  },
  {
    label: 'Submitted – documentation pending',
    ids: [QUEUE_STATUS_MAP.REQUEST_QUEUE.SUBMITTED_DOCUMENTATION_PENDING, QUEUE_STATUS_MAP.EXCEPTION_QUEUE.SUBMITTED_DOCUMENTATION_PENDING],
  },
  {
    label: 'Pending',
    ids: [QUEUE_STATUS_MAP.REQUEST_QUEUE.PENDING, QUEUE_STATUS_MAP.EXCEPTION_QUEUE.PENDING],
  },
  {
    label: 'Under review',
    ids: [QUEUE_STATUS_MAP.REQUEST_QUEUE.UNDER_REVIEW, QUEUE_STATUS_MAP.EXCEPTION_QUEUE.UNDER_REVIEW],
  },
  {
    label: 'Pending payment',
    ids: [QUEUE_STATUS_MAP.FISCAL_QUEUE.PENDING_PAYMENTS],
  },
  {
    label: 'Expired',
    ids: [QUEUE_STATUS_MAP.AGING_QUEUE.EXPIRED],
  },
];
//------------
export interface AgingRow {
  requestNumber: number;
  recipientId: number;
  tripConfirmationNo: string | null;
  consumerName: string;
  submittedDate: string;
  dueDate: string;
  followUpDate: string | null;
  createdBy: string;
  requestStatus: string;
  daysRemaining: number;
  agingCode: AgingCode;
  agingLabel: string;
  amountDue: number;
  payeeName: string | null;
  assignedTo?: string | null;
}
export const AGING_CODE = {
  RED: 'RED',
  YELLOW: 'YELLOW',
} as const;

export interface AgingGroupRow {
  group: AgingGroup;
  rows: AgingRow[];
  collapsed: boolean;
}
export interface QueueStatus {
  id: string;
  label: string;
  count: number;
}
export interface PaymentTable {
  recipientId: number;
  requestNumber: number;
  tripConfirmationNo: string | null;
  consumerName: string;
  payeeName: string;
  submittedDate: string;
  dueDate: string;
  amountDue: number;
}

export interface QueueRequestDto {
  queueStatusMappingIds: number[];
  startDate?: string | null;
  endDate?: string | null;
}

export interface QueueTableRecord {
  recipientId: number;
  requestNumber: number;
  tripConfirmationNo: string | null;
  consumerName: string;
  appointmentDate: string;
  submittedDate: string;
  dueDate: string;
  createdBy: string;
  assignedTo?: string | null;
}
export interface PaymentGroup {
  recipientId: number;
  consumerName: string;
  payeeName: string;
  payments: PaymentTable[];
  totalAmount: number;
  isExpanded: boolean;
  hasMultiplePayments: boolean;
}

export enum QueueTabEnum {
  MY_QUEUE = 'my-queue',
  REQUESTS = 'requests',
  EXCEPTION = 'exception',
  FISCAL = 'fiscal',
  AGING = 'aging',
}
export const QUEUE_PANELS = {
  SUBMITTED_AGENCY: 'submitted-agency',
  SUBMITTED_CONSUMER: 'submitted-consumer',
  SUBMITTED_DOCUMENTATION: 'submitted-documentation',
  UNDER_REVIEW: 'under-review',
  PENDING: 'pending',
  NO_RESPONSE: 'no-response',
  RETURNED: 'returned',
} as const;
export type AgingCode = (typeof AGING_CODE)[keyof typeof AGING_CODE];
export type QueueStatusMap = typeof QUEUE_STATUS_MAP;
export type QueueName = keyof QueueStatusMap;
export type QueueStatusId<T extends QueueName> = QueueStatusMap[T][keyof QueueStatusMap[T]];
export interface PaymentInfoDto {
  paymentInfoId: number;
  recipientId: number;
  tripRequestId: number;
  paymentMethodId: number;
  otherPaymentMethod?: string;
  checkNumber?: string;
  paymentCheckAmount: number;
  paidDate?: string;
  checkDate?: string;
  mailCheckDate?: string;
  payeeName: string;
  paymentMethodEnum: PaymentMethodEnum;
}
export enum PaymentMethodEnum {
  PhysicalCheck = 1,
  ACH = 2,
  DebitCard = 3,
  Other = 4,
}
export enum QUEUE_TYPES {
  REQUEST_QUEUE = 1,
  EXCEPTION_QUEUE = 2,
  FISCAL_QUEUE = 3,
  AGING_QUEUE = 4,
  MY_QUEUE = 5,
}
export interface QueueExportRequestDto {
  queueTypeId: number;
  queueStatusMappingId?: number;
  startDate?: string | null;
  endDate?: string | null;
}
